import {  Component, Input, OnInit, ElementRef, ViewChild, forwardRef, OnDestroy, Injector, DoCheck, HostBinding, AfterViewInit, ViewContainerRef} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, NgControl, FormControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import Quill from 'quill';
import Delta, { Op } from 'quill-delta';
import Link from 'quill/formats/link';
import Toolbar from 'quill/modules/toolbar';
import { LinkCreatorComponent } from './link-creator/link-creator.component';


export class SimpleLink extends Link {
  static override create(value: string) {
    const node = super.create(value);
    node.setAttribute('href', this.sanitize(value));
    //node.setAttribute('target', '_blank');
    node.removeAttribute('target');
    //node.setAttribute('rel', 'noopener noreferrer');
    node.removeAttribute('rel');

    /////////////////////////////////////////////////////////////
    // custom link handler (comment this part to use default, and remove ql-tooltip css rule)
    /////////////////////////////////////////////////////////////
    SimpleLink.attachCustomLinkHandler(node);
    /////////////////////////////////////////////////////////////

    return node;
  }

  /**
   * attach click event to call custom link handler for editing of existing links
   * note: we should hide the native quilljs .ql-tooltip through css to avoid it from popping up
   * 
   * @param node 
   */
  private static attachCustomLinkHandler(node: HTMLElement) {
    node?.addEventListener('click', (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const selection = window.getSelection();
      //clear any current selection
      selection?.removeAllRanges();
      const range = document.createRange();
      //select link node
      range.selectNodeContents(node);
      selection?.addRange(range);

      //now we programmatically click the toolbar button
      let parentNode = node.parentElement;
      let linkButton = parentNode ? parentNode.querySelector(".ql-link") : null;
      while (parentNode && !linkButton) {
        parentNode = parentNode.parentElement;
        linkButton = parentNode ? parentNode.querySelector(".ql-link") : null;
      }
      if (linkButton) {
        linkButton.dispatchEvent(new Event('click'));
      }

      return false;
    });
  }
}

const SELECTOR = 'quill-material';

@Component({
  selector: SELECTOR,
  imports: [LinkCreatorComponent],
  templateUrl: './quill-material.component.html',
  styleUrl: './quill-material.component.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => QuillMaterialComponent),
    multi: true
  },

  {
    provide: MatFormFieldControl,
    useExisting: forwardRef(() => QuillMaterialComponent)
  }],
  host: {
    '[id]': 'id',
    '[attr.aria-describedby]': 'describedBy'
  }
})
export class QuillMaterialComponent implements OnInit, DoCheck, OnDestroy, ControlValueAccessor, MatFormFieldControl<any> {
  static nextId = 0;

  @HostBinding('class.addingLink')
  public customLinkHandlerData: { linkUrl: string, linkName: string } | null = null;
  private updatingTextForLink: boolean = false;
  private linkTextIndex: number = -1;
  private linkTextLength: number = -1;

  @HostBinding('class.empty')
  get empty() {
    //.ql-blank is not peresent when there is one or more blank spaces
    //return this.container.nativeElement.querySelector('#editor > .ql-editor.ql-blank') != null
    const text = this.editor.getText().trim();
    return text ? false : true;
  }

  @HostBinding()
  public id = `quill-material-${QuillMaterialComponent.nextId++}`;

  @ViewChild('container', { read: ElementRef, static: true })
  public container: ElementRef;

  stateChanges = new Subject<void>();

  quill: any = Quill;
  editor: any;
  controlType = 'quill-material';
  errorState = false;
  ngControl: NgControl = null;
  touched = false;
  focused = false;

  public _value: any;
  get value(): any {
    return this._value;
  }
  @Input()
  set value(value) {
    this._value = value;
    //this.editor.setContents(this._value);
    this.editor.clipboard.dangerouslyPasteHTML(value ? value : '', 'api')
    this.onChange(value);
    this.stateChanges.next();
  }

  public _placeholder: string;
  get placeholder() {
    return this._placeholder;
  }
  @Input()
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  public _required = false;
  get required() {
    return this._required;
  }
  @Input()
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  public _disabled = false;
  get disabled() {
    return this._disabled;
  }
  @Input()
  set disabled(disabled) {
    this._disabled = coerceBooleanProperty(disabled);
    this.stateChanges.next();
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    const shouldFloaf = this.focused || !this.empty || this.customLinkHandlerData !== null;

    if (this._placeholder && this._placeholder.length > 0 && this.empty && this.editor) {
      if (shouldFloaf) {
        this.container.nativeElement.querySelector('#editor')?.setAttribute('data-placeholder', this._placeholder);
      } else {
        this.container.nativeElement.querySelector('#editor')?.setAttribute('data-placeholder', '');
      }
    }
    return shouldFloaf;
  }

  @HostBinding('attr.aria-describedby') describedBy = '';
  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  constructor(
    public elRef: ElementRef,
    public injector: Injector,
    public fm: FocusMonitor,
    private viewContainerRef: ViewContainerRef) {

    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;

      //kke, making sure the parent mat field has status touched
      //at startup this.ngControl.touched can be null
      if (this.focused && this.ngControl !== null && typeof this.ngControl.touched === 'boolean' && !this.ngControl.touched) {
        this.onTouched();
      }

      this.stateChanges.next();
    });
  }

  ngOnInit(): void {
    // avoid Cyclic Dependency
    this.ngControl = this.injector.get(NgControl);
    if (this.ngControl != null) { this.ngControl.valueAccessor = this; }

    Quill.register(SimpleLink);

    const editorRef = this.container.nativeElement.querySelector('#editor');
    const toolbarRef = this.container.nativeElement.querySelector('#toolbar');

    this.editor = new Quill(editorRef, {
      theme: 'snow',
      /*
      we add the placeholder dynamically, depending on a floating label
      placeholder: this._placeholder ? null : this._placeholder,
      */
      modules: {
        toolbar: toolbarRef
      }
    });

    const toolbar = this.editor.getModule('toolbar') as Toolbar;
    toolbar.addHandler('link', (value: boolean) => {
      this.customLinkHandler(value);
    });

    this.editor.on('text-change', () => {
      //kke
      //sometimes "this.ngControl.touched" stayed null (or false), we changed it by adding a test in the constructor , fm.monitor
      //if it still doesn't work, maybe put this if statement in comments
      if (this.ngControl.touched) {
        this.onChange(this.getValue());
      }
    });
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched /*&& !this.focused*/;
      this.stateChanges.next();
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  //this happens on startop, passing content to editor
  writeValue(contents: any): void {
    if (this.editor && contents) {
      //const delta = this.editor.clipboard.convert(contents); // convert html to delta
      //this.editor.setContents(delta);
      //kke
      this.editor.clipboard.dangerouslyPasteHTML(contents ? contents : '', 'api')
      this._value = contents;
    }
  }

  onChange = (delta: any) => { };
  registerOnChange(fn: (v: any) => void): void {
    //this.onChange = fn;
    //kke
    this.onChange = (v: any) => {
      if (this.customLinkHandlerData && !this.updatingTextForLink) {
        //when the user opened the link creator form, but afterwards continues in the editor, then we close the link creator form
        this.customLinkHandlerData = null;
      }
      fn(v);
    };
  }

  onTouched = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onContainerClick(event: MouseEvent) {
    if (!this.focused) {
      this.editor.focus();
      this.focused = true;
      this.stateChanges.next();
    }
  }

  private getValue(): any | undefined {
    if (!this.editor) {
      return undefined;
    }

    const delta: any = this.editor.getContents();
    if (this.isEmpty(delta)) {
      return undefined;
    }

    /*
    const converter = new QuillDeltaToHtmlConverter(delta.ops, {});
    const html = converter.convert();
    return html;
    */
    //kke
    let html = this.editor.getSemanticHTML().replace(/(&nbsp;)/gim, ' ').replace(/(<p><\/p>)/gim, '<br />');
    return html;
  }

  private isEmpty(contents: any): boolean {
    if (contents.ops.length > 1) {
      return false;
    }

    const opsTypes: Array<string> = Object.keys(contents.ops[0]);

    if (opsTypes.length > 1) {
      return false;
    }

    if (opsTypes[0] !== 'insert') {
      return false;
    }

    if (contents.ops[0].insert !== '\n') {
      return false;
    }

    return true;
  }


  private customLinkHandler(originalHandlerValue: boolean) {
    //if (originalHandlerValue){
    //default add event when a text was selected without link
    //}
    //} else {
    //default remove event after selecting existing link
    //quill.format('link', false, 'user');
    //}

    if (this.customLinkHandlerData !== null) {
      this.customLinkHandlerData = null;
      this.linkTextIndex = -1;
      this.linkTextLength = - 1;
    }

    this.linkTextIndex = this.editor.getSelection() && typeof this.editor.getSelection().index === 'number' ? this.editor.getSelection().index : -1;
    this.linkTextLength = this.editor.getSelection() && typeof this.editor.getSelection().length === 'number' ? this.editor.getSelection().length : -1;

    if (this.linkTextIndex === -1 || this.linkTextLength === -1) {
      return;
    }

    let selectedText = this.editor.getText().substring(this.linkTextIndex, this.linkTextIndex + this.linkTextLength);

    //sometimes a space after the word gets selected too
    //remove trailing spaces from selection
    //when you only select spaces, deselect spaces
    if (selectedText.length !== selectedText.trim().length) {
      let trailingSpaces = selectedText.length - selectedText.trimEnd().length;
      this.editor.setSelection(this.linkTextIndex, selectedText.length - trailingSpaces, 'user');

      this.linkTextIndex = this.editor.getSelection() && typeof this.editor.getSelection().index === 'number' ? this.editor.getSelection().index : -1;
      this.linkTextLength = this.editor.getSelection() && typeof this.editor.getSelection().length === 'number' ? this.editor.getSelection().length : -1;

      if (this.linkTextIndex === -1 || this.linkTextLength === -1) {
        return;
      }

      selectedText = this.editor.getText().substring(this.linkTextIndex, this.linkTextIndex + this.linkTextLength);
    }

    let selectedUrl = selectedText && this.editor.getFormat(this.linkTextIndex, this.linkTextLength) && this.editor.getFormat(this.linkTextIndex, this.linkTextLength)['link'] ? this.editor.getFormat(this.linkTextIndex, this.linkTextLength)['link'] : '';

    //this.closeCustomLinkHandler(null);

    this.customLinkHandlerData = { linkUrl: selectedUrl, linkName: selectedText };

  }

  public closeCustomLinkHandler(saveData: { linkUrl: string, linkName: string } | null) {

    if (this.updatingTextForLink) {
      return;
    }

    this.updatingTextForLink = true;

    if (this.linkTextIndex > -1 && this.linkTextLength > -1 && this.editor.getText().length >= (this.linkTextIndex + this.linkTextLength)
      && saveData && typeof saveData.linkUrl === 'string' && typeof saveData.linkName === 'string') {

      if (saveData.linkUrl.length > 0 && saveData.linkName.length > 0) {
        //user updated link text in popup?
        if (saveData.linkName !== this.customLinkHandlerData.linkName) {
          this.editor.updateContents(new Delta()
            .retain(this.linkTextIndex)
            .delete(this.linkTextLength)
            .insert(saveData.linkName));
          this.editor.setSelection(this.linkTextIndex, saveData.linkName.length, 'user');
        }

        this.editor.format('link', saveData.linkUrl, 'user');

      } else if (saveData.linkUrl === 'remove_link') {
        this.editor.format('link', false, 'user');
      }

    } else if (!this.editor.hasFocus()) {
      this.editor.focus();
    }

    this.customLinkHandlerData = null;
    this.linkTextIndex = -1;
    this.linkTextLength = - 1;

    //timeout for onChange listener
    window.setTimeout(() => {
      this.updatingTextForLink = false;
    }, 500);
  }

}